<template>
  <div class="child-frame">
    <h1 class="seo-headline">How the Electoral College works</h1>
    <article class="container-fluid">
      <section class="taskbar-container wider">
        <div class="taskbar">
          <div class="logo"><img src="img/taskbar-logo.png"></div>
          <div class="task-item mail hidden-sm-down"><a href="https://tmsnrt.rs/3ic6mt9" target="_blank">Voting by mail</a></div>
          <div class="task-item policies hidden-sm-down selected"><a href="https://tmsnrt.rs/2Izj6wR" target="_blank">The Electoral College</a></div>
          <div class="task-item data hidden-sm-down"><a href="https://graphics.reuters.com/USA-ELECTION/DATA-VISUAL/yxmvjjgojvr/index.html" target="_blank">Politics and data</a></div>
          <div class="task-separator hidden-sm-down"></div>
          <div class="task-item policies hidden-sm-down"><a href="https://graphics.reuters.com/USA-ELECTION/POLICY/ygdpzwarjvw/index.html" target="_blank">Key issues</a></div>
        </div>
      </section>
      <div class="text-center" id="topper"><img src="img/explainer/electoral-college.png"></div>
      <section class="headline">
        <h2 class="pb-4">How the Electoral College works</h2>
        <p>A guide to the complex system the United States uses to select a president</p>
        <aside class="article-metadata">
          <div class="byline-container">
            <div class="byline">By <strong><a href="https://twitter.com/MinamiFunakoshi">Minami Funakoshi</a></strong>, <strong>Wen Foo</strong> and <strong>Jan Wolfe</strong></div>
          </div>
          <div class="dateline-container">
            <div class="published uppercase">Published Oct. 16, 2020</div>
          </div>
        </aside>
      </section>
      <section class="body-text">
        <p>In the United States, a candidate becomes president not by winning a majority of the national popular vote but through a system called the Electoral College, which allots electoral votes to the 50 states and the District of Columbia
          largely based on their population.</p>
        <p>There are a total of 538 electoral votes, or &#x201C;electors,&#x201D; meaning a candidate needs to secure 270 to win.</p>
      </section>
      <section class="map pb-4">
        <component is="style" media="screen,print">
          #g-votesMap-box,
          #g-votesMap-box .g-artboard {
            margin: 0 auto;
          }

          #g-votesMap-box p {
            margin: 0;
          }

          #g-votesMap-box .g-aiAbs {
            position: absolute;
          }

          #g-votesMap-box .g-aiImg {
            position: absolute;
            top: 0;
            display: block;
            width: 100% !important;
            height: 100%;
            background-size: contain;
            background-repeat: no-repeat;
          }

          #g-votesMap-box .g-aiSymbol {
            position: absolute;
            box-sizing: border-box;
          }

          #g-votesMap-box .g-aiPointText p {
            white-space: nowrap;
          }

          #g-votesMap-sm {
            position: relative;
            overflow: hidden;
          }

          #g-votesMap-sm p {
            font-family: "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif;
            font-weight: 700;
            line-height: 27px;
            height: auto;
            filter: alpha(opacity=100);
            -ms-filter: progid:DXImageTransform.Microsoft.Alpha(Opacity=100);
            opacity: 1;
            letter-spacing: 0em;
            font-size: 14px;
            text-align: left;
            color: rgb(0, 0, 0);
            text-transform: none;
            padding-bottom: 0;
            padding-top: 0;
            mix-blend-mode: normal;
            font-style: normal;
            position: static;
          }

          #g-votesMap-sm .g-pstyle0 {
            height: 27px;
          }

          #g-votesMap-sm .g-pstyle1 {
            font-weight: 300;
            line-height: 18px;
            height: 18px;
          }

          #g-votesMap-md {
            position: relative;
            overflow: hidden;
          }

          #g-votesMap-md p {
            font-family: "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif;
            font-weight: 700;
            line-height: 32px;
            height: auto;
            filter: alpha(opacity=100);
            -ms-filter: progid:DXImageTransform.Microsoft.Alpha(Opacity=100);
            opacity: 1;
            letter-spacing: 0em;
            font-size: 16px;
            text-align: left;
            color: rgb(0, 0, 0);
            text-transform: none;
            padding-bottom: 0;
            padding-top: 0;
            mix-blend-mode: normal;
            font-style: normal;
            position: static;
          }

          #g-votesMap-md .g-pstyle0 {
            height: 32px;
          }

          #g-votesMap-md .g-pstyle1 {
            font-weight: 300;
            line-height: 22px;
            height: 22px;
            font-size: 14px;
          }

          #g-votesMap-xs {
            position: relative;
            overflow: hidden;
          }

          #g-votesMap-xs p {
            font-family: "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif;
            font-weight: 700;
            line-height: 16px;
            height: auto;
            filter: alpha(opacity=100);
            -ms-filter: progid:DXImageTransform.Microsoft.Alpha(Opacity=100);
            opacity: 1;
            letter-spacing: 0em;
            font-size: 12px;
            text-align: left;
            color: rgb(0, 0, 0);
            text-transform: none;
            padding-bottom: 0;
            padding-top: 0;
            mix-blend-mode: normal;
            font-style: normal;
            position: static;
          }

          #g-votesMap-xs .g-pstyle0 {
            height: 16px;
          }

          #g-votesMap-xs .g-pstyle1 {
            font-weight: 300;
            line-height: 11px;
            height: 11px;
          }

          /* Custom CSS */
        </component>
        <div id="g-votesMap-box" class="ai2html">
          <div id="g-votesMap-sm" class="g-artboard" style="max-width: 689px;max-height: 551px" data-aspect-ratio="1.252" data-min-width="510" data-max-width="689">
            <div style="padding: 0 0 79.8999% 0;"></div>
            <div id="g-votesMap-sm-img" class="g-aiImg" alt style="background-image: url(img/graphics/votesMap-sm.png);"></div>
            <div id="g-ai0-1" class="g-sm g-aiAbs g-aiPointText" style="top:5.0345%;margin-top:-14.5px;left:95.7904%;width:39px;">
              <p class="g-pstyle0">ME</p>
            </div>
            <div id="g-ai0-2" class="g-sm g-aiAbs g-aiPointText" style="top:7.5119%;margin-top:-10.6px;left:96.9006%;width:28px;">
              <p class="g-pstyle1">4</p>
            </div>
            <div id="g-ai0-3" class="g-sm g-aiAbs g-aiPointText" style="top:9.2064%;margin-top:-14.5px;left:91.4744%;width:37px;">
              <p class="g-pstyle0">VT</p>
            </div>
            <div id="g-ai0-4" class="g-sm g-aiAbs g-aiPointText" style="top:10.6788%;margin-top:-14.5px;left:87.5856%;width:40px;">
              <p class="g-pstyle0">NH</p>
            </div>
            <div id="g-ai0-5" class="g-sm g-aiAbs g-aiPointText" style="top:11.6838%;margin-top:-10.6px;left:92.348%;width:28px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai0-6" class="g-sm g-aiAbs g-aiPointText" style="top:13.1562%;margin-top:-10.6px;left:88.732%;width:28px;">
              <p class="g-pstyle1">4</p>
            </div>
            <div id="g-ai0-7" class="g-sm g-aiAbs g-aiPointText" style="top:16.814%;margin-top:-14.5px;left:90.2753%;width:40px;">
              <p class="g-pstyle0">MA</p>
            </div>
            <div id="g-ai0-8" class="g-sm g-aiAbs g-aiPointText" style="top:19.2913%;margin-top:-10.6px;left:90.9049%;width:35px;">
              <p class="g-pstyle1">11</p>
            </div>
            <div id="g-ai0-9" class="g-sm g-aiAbs g-aiPointText" style="top:22.7037%;margin-top:-14.5px;left:80.2102%;width:38px;">
              <p class="g-pstyle0">NY</p>
            </div>
            <div id="g-ai0-10" class="g-sm g-aiAbs g-aiPointText" style="top:23.6853%;margin-top:-14.5px;left:88.0134%;width:37px;">
              <p class="g-pstyle0">CT</p>
            </div>
            <div id="g-ai0-11" class="g-sm g-aiAbs g-aiPointText" style="top:25.1578%;margin-top:-14.5px;left:93.9618%;width:34px;">
              <p class="g-pstyle0">RI</p>
            </div>
            <div id="g-ai0-12" class="g-sm g-aiAbs g-aiPointText" style="top:25.1811%;margin-top:-10.6px;left:80.6621%;width:35px;">
              <p class="g-pstyle1">29</p>
            </div>
            <div id="g-ai0-13" class="g-sm g-aiAbs g-aiPointText" style="top:26.1627%;margin-top:-10.6px;left:88.8949%;width:28px;">
              <p class="g-pstyle1">7</p>
            </div>
            <div id="g-ai0-14" class="g-sm g-aiAbs g-aiPointText" style="top:27.6351%;margin-top:-10.6px;left:94.5795%;width:28px;">
              <p class="g-pstyle1">4</p>
            </div>
            <div id="g-ai0-15" class="g-sm g-aiAbs g-aiPointText" style="top:28.8388%;margin-top:-14.5px;left:54.1705%;width:36px;">
              <p class="g-pstyle0">MI</p>
            </div>
            <div id="g-ai0-16" class="g-sm g-aiAbs g-aiPointText" style="top:31.807%;margin-top:-10.6px;left:54.4545%;width:35px;">
              <p class="g-pstyle1">16</p>
            </div>
            <div id="g-ai0-17" class="g-sm g-aiAbs g-aiPointText" style="top:32.2745%;margin-top:-14.5px;left:41.9644%;width:37px;">
              <p class="g-pstyle0">WI</p>
            </div>
            <div id="g-ai0-18" class="g-sm g-aiAbs g-aiPointText" style="top:33.7469%;margin-top:-14.5px;left:74.4968%;width:37px;">
              <p class="g-pstyle0">PA</p>
            </div>
            <div id="g-ai0-19" class="g-sm g-aiAbs g-aiPointText" style="top:34.7286%;margin-top:-14.5px;left:63.2778%;width:40px;">
              <p class="g-pstyle0">OH</p>
            </div>
            <div id="g-ai0-20" class="g-sm g-aiAbs g-aiPointText" style="top:35.2427%;margin-top:-10.6px;left:42.312%;width:35px;">
              <p class="g-pstyle1">10</p>
            </div>
            <div id="g-ai0-21" class="g-sm g-aiAbs g-aiPointText" style="top:36.2243%;margin-top:-10.6px;left:74.8579%;width:35px;">
              <p class="g-pstyle1">20</p>
            </div>
            <div id="g-ai0-22" class="g-sm g-aiAbs g-aiPointText" style="top:37.4513%;margin-top:-10.6px;left:63.9288%;width:35px;">
              <p class="g-pstyle1">18</p>
            </div>
            <div id="g-ai0-23" class="g-sm g-aiAbs g-aiPointText" style="top:38.1642%;margin-top:-14.5px;left:6.4059%;width:41px;">
              <p class="g-pstyle0">WA</p>
            </div>
            <div id="g-ai0-24" class="g-sm g-aiAbs g-aiPointText" style="top:38.1642%;margin-top:-14.5px;left:36.6395%;width:41px;">
              <p class="g-pstyle0">MN</p>
            </div>
            <div id="g-ai0-25" class="g-sm g-aiAbs g-aiPointText" style="top:38.655%;margin-top:-14.5px;left:54.0521%;width:35px;">
              <p class="g-pstyle0">IN</p>
            </div>
            <div id="g-ai0-26" class="g-sm g-aiAbs g-aiPointText" style="top:39.1459%;margin-top:-14.5px;left:82.869%;width:38px;">
              <p class="g-pstyle0">NJ</p>
            </div>
            <div id="g-ai0-27" class="g-sm g-aiAbs g-aiPointText" style="top:40.6416%;margin-top:-10.6px;left:7.099%;width:35px;">
              <p class="g-pstyle1">12</p>
            </div>
            <div id="g-ai0-28" class="g-sm g-aiAbs g-aiPointText" style="top:41.1324%;margin-top:-10.6px;left:37.3764%;width:35px;">
              <p class="g-pstyle1">10</p>
            </div>
            <div id="g-ai0-29" class="g-sm g-aiAbs g-aiPointText" style="top:41.1091%;margin-top:-14.5px;left:46.6621%;width:33px;">
              <p class="g-pstyle0">IL</p>
            </div>
            <div id="g-ai0-30" class="g-sm g-aiAbs g-aiPointText" style="top:41.3778%;margin-top:-10.6px;left:54.2155%;width:35px;">
              <p class="g-pstyle1">11</p>
            </div>
            <div id="g-ai0-31" class="g-sm g-aiAbs g-aiPointText" style="top:42.114%;margin-top:-10.6px;left:64.1071%;width:28px;">
              <p class="g-pstyle1">5</p>
            </div>
            <div id="g-ai0-32" class="g-sm g-aiAbs g-aiPointText" style="top:42.114%;margin-top:-10.6px;left:83.4528%;width:35px;">
              <p class="g-pstyle1">14</p>
            </div>
            <div id="g-ai0-33" class="g-sm g-aiAbs g-aiPointText" style="top:42.0907%;margin-top:-14.5px;left:60.1495%;width:40px;">
              <p class="g-pstyle0">WV</p>
            </div>
            <div id="g-ai0-34" class="g-sm g-aiAbs g-aiPointText" style="top:42.3361%;margin-top:-14.5px;left:71.7666%;width:41px;">
              <p class="g-pstyle0">MD</p>
            </div>
            <div id="g-ai0-35" class="g-sm g-aiAbs g-aiPointText" style="top:43.5865%;margin-top:-10.6px;left:46.4252%;width:35px;">
              <p class="g-pstyle1">20</p>
            </div>
            <div id="g-ai0-36" class="g-sm g-aiAbs g-aiPointText" style="top:44.2994%;margin-top:-14.5px;left:77.4131%;width:38px;">
              <p class="g-pstyle0">DE</p>
            </div>
            <div id="g-ai0-37" class="g-sm g-aiAbs g-aiPointText" style="top:45.0589%;margin-top:-10.6px;left:72.4659%;width:35px;">
              <p class="g-pstyle1">10</p>
            </div>
            <div id="g-ai0-38" class="g-sm g-aiAbs g-aiPointText" style="top:45.281%;margin-top:-14.5px;left:7.3351%;width:39px;">
              <p class="g-pstyle0">OR</p>
            </div>
            <div id="g-ai0-39" class="g-sm g-aiAbs g-aiPointText" style="top:46.5313%;margin-top:-10.6px;left:78.3655%;width:28px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai0-40" class="g-sm g-aiAbs g-aiPointText" style="top:46.7767%;margin-top:-10.6px;left:40.777%;width:28px;">
              <p class="g-pstyle1">6</p>
            </div>
            <div id="g-ai0-41" class="g-sm g-aiAbs g-aiPointText" style="top:46.7534%;margin-top:-14.5px;left:37.4299%;width:34px;">
              <p class="g-pstyle0">IA</p>
            </div>
            <div id="g-ai0-42" class="g-sm g-aiAbs g-aiPointText" style="top:47.4896%;margin-top:-14.5px;left:58.5133%;width:37px;">
              <p class="g-pstyle0">KY</p>
            </div>
            <div id="g-ai0-43" class="g-sm g-aiAbs g-aiPointText" style="top:47.4896%;margin-top:-14.5px;left:12.7765%;width:34px;">
              <p class="g-pstyle0">ID</p>
            </div>
            <div id="g-ai0-44" class="g-sm g-aiAbs g-aiPointText" style="top:48.0037%;margin-top:-10.6px;left:22.0549%;width:28px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai0-45" class="g-sm g-aiAbs g-aiPointText" style="top:48.0037%;margin-top:-10.6px;left:28.6012%;width:28px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai0-46" class="g-sm g-aiAbs g-aiPointText" style="top:47.9804%;margin-top:-14.5px;left:69.7899%;width:37px;">
              <p class="g-pstyle0">VA</p>
            </div>
            <div id="g-ai0-47" class="g-sm g-aiAbs g-aiPointText" style="top:48.0037%;margin-top:-10.6px;left:8.4293%;width:28px;">
              <p class="g-pstyle1">7</p>
            </div>
            <div id="g-ai0-48" class="g-sm g-aiAbs g-aiPointText" style="top:47.9804%;margin-top:-14.5px;left:18.0607%;width:40px;">
              <p class="g-pstyle0">MT</p>
            </div>
            <div id="g-ai0-49" class="g-sm g-aiAbs g-aiPointText" style="top:47.9804%;margin-top:-14.5px;left:24.5805%;width:39px;">
              <p class="g-pstyle0">ND</p>
            </div>
            <div id="g-ai0-50" class="g-sm g-aiAbs g-aiPointText" style="top:49.967%;margin-top:-10.6px;left:59.3888%;width:28px;">
              <p class="g-pstyle1">8</p>
            </div>
            <div id="g-ai0-51" class="g-sm g-aiAbs g-aiPointText" style="top:50.2124%;margin-top:-10.6px;left:13.4215%;width:28px;">
              <p class="g-pstyle1">4</p>
            </div>
            <div id="g-ai0-52" class="g-sm g-aiAbs g-aiPointText" style="top:50.4578%;margin-top:-10.6px;left:70.1509%;width:35px;">
              <p class="g-pstyle1">13</p>
            </div>
            <div id="g-ai0-53" class="g-sm g-aiAbs g-aiPointText" style="top:50.9486%;margin-top:-10.6px;left:22.2125%;width:28px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai0-54" class="g-sm g-aiAbs g-aiPointText" style="top:50.9486%;margin-top:-10.6px;left:28.7588%;width:28px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai0-55" class="g-sm g-aiAbs g-aiPointText" style="top:50.9253%;margin-top:-14.5px;left:18.3731%;width:40px;">
              <p class="g-pstyle0">WY</p>
            </div>
            <div id="g-ai0-56" class="g-sm g-aiAbs g-aiPointText" style="top:50.9253%;margin-top:-14.5px;left:24.5805%;width:38px;">
              <p class="g-pstyle0">SD</p>
            </div>
            <div id="g-ai0-57" class="g-sm g-aiAbs g-aiPointText" style="top:51.1707%;margin-top:-14.5px;left:32.1379%;width:38px;">
              <p class="g-pstyle0">NE</p>
            </div>
            <div id="g-ai0-58" class="g-sm g-aiAbs g-aiPointText" style="top:52.421%;margin-top:-10.6px;left:40.7772%;width:28px;">
              <p class="g-pstyle1">6</p>
            </div>
            <div id="g-ai0-59" class="g-sm g-aiAbs g-aiPointText" style="top:52.3977%;margin-top:-14.5px;left:37.4301%;width:37px;">
              <p class="g-pstyle0">KS</p>
            </div>
            <div id="g-ai0-60" class="g-sm g-aiAbs g-aiPointText" style="top:53.3794%;margin-top:-14.5px;left:53.2196%;width:38px;">
              <p class="g-pstyle0">TN</p>
            </div>
            <div id="g-ai0-61" class="g-sm g-aiAbs g-aiPointText" style="top:53.8935%;margin-top:-10.6px;left:33.1276%;width:28px;">
              <p class="g-pstyle1">5</p>
            </div>
            <div id="g-ai0-62" class="g-sm g-aiAbs g-aiPointText" style="top:54.1156%;margin-top:-14.5px;left:45.4283%;width:41px;">
              <p class="g-pstyle0">MO</p>
            </div>
            <div id="g-ai0-63" class="g-sm g-aiAbs g-aiPointText" style="top:54.8518%;margin-top:-14.5px;left:65.7357%;width:39px;">
              <p class="g-pstyle0">NC</p>
            </div>
            <div id="g-ai0-64" class="g-sm g-aiAbs g-aiPointText" style="top:56.0788%;margin-top:-14.5px;left:13.5686%;width:38px;">
              <p class="g-pstyle0">NV</p>
            </div>
            <div id="g-ai0-65" class="g-sm g-aiAbs g-aiPointText" style="top:56.0788%;margin-top:-14.5px;left:18.2165%;width:38px;">
              <p class="g-pstyle0">UT</p>
            </div>
            <div id="g-ai0-66" class="g-sm g-aiAbs g-aiPointText" style="top:56.0788%;margin-top:-14.5px;left:24.0054%;width:38px;">
              <p class="g-pstyle0">CO</p>
            </div>
            <div id="g-ai0-67" class="g-sm g-aiAbs g-aiPointText" style="top:56.3475%;margin-top:-10.6px;left:53.8649%;width:35px;">
              <p class="g-pstyle1">11</p>
            </div>
            <div id="g-ai0-68" class="g-sm g-aiAbs g-aiPointText" style="top:56.5929%;margin-top:-10.6px;left:46.1684%;width:35px;">
              <p class="g-pstyle1">10</p>
            </div>
            <div id="g-ai0-69" class="g-sm g-aiAbs g-aiPointText" style="top:56.815%;margin-top:-14.5px;left:81.8729%;width:38px;">
              <p class="g-pstyle0">DC</p>
            </div>
            <div id="g-ai0-70" class="g-sm g-aiAbs g-aiPointText" style="top:56.815%;margin-top:-14.5px;left:35.0235%;width:39px;">
              <p class="g-pstyle0">OK</p>
            </div>
            <div id="g-ai0-71" class="g-sm g-aiAbs g-aiPointText" style="top:57.5746%;margin-top:-10.6px;left:66.2485%;width:35px;">
              <p class="g-pstyle1">15</p>
            </div>
            <div id="g-ai0-72" class="g-sm g-aiAbs g-aiPointText" style="top:58.3108%;margin-top:-10.6px;left:14.577%;width:28px;">
              <p class="g-pstyle1">6</p>
            </div>
            <div id="g-ai0-73" class="g-sm g-aiAbs g-aiPointText" style="top:58.3108%;margin-top:-10.6px;left:19.2162%;width:28px;">
              <p class="g-pstyle1">6</p>
            </div>
            <div id="g-ai0-74" class="g-sm g-aiAbs g-aiPointText" style="top:58.3108%;margin-top:-10.6px;left:25.0151%;width:28px;">
              <p class="g-pstyle1">9</p>
            </div>
            <div id="g-ai0-75" class="g-sm g-aiAbs g-aiPointText" style="top:59.0237%;margin-top:-14.5px;left:41.4476%;width:38px;">
              <p class="g-pstyle0">AR</p>
            </div>
            <div id="g-ai0-76" class="g-sm g-aiAbs g-aiPointText" style="top:59.2924%;margin-top:-10.6px;left:36.1264%;width:28px;">
              <p class="g-pstyle1">7</p>
            </div>
            <div id="g-ai0-77" class="g-sm g-aiAbs g-aiPointText" style="top:59.2924%;margin-top:-10.6px;left:82.8674%;width:28px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai0-78" class="g-sm g-aiAbs g-aiPointText" style="top:61.2323%;margin-top:-14.5px;left:5.3408%;width:37px;">
              <p class="g-pstyle0">CA</p>
            </div>
            <div id="g-ai0-79" class="g-sm g-aiAbs g-aiPointText" style="top:61.501%;margin-top:-10.6px;left:42.4119%;width:28px;">
              <p class="g-pstyle1">6</p>
            </div>
            <div id="g-ai0-80" class="g-sm g-aiAbs g-aiPointText" style="top:61.9685%;margin-top:-14.5px;left:62.5301%;width:37px;">
              <p class="g-pstyle0">SC</p>
            </div>
            <div id="g-ai0-81" class="g-sm g-aiAbs g-aiPointText" style="top:62.9502%;margin-top:-14.5px;left:20.121%;width:41px;">
              <p class="g-pstyle0">NM</p>
            </div>
            <div id="g-ai0-82" class="g-sm g-aiAbs g-aiPointText" style="top:63.9551%;margin-top:-10.6px;left:5.7466%;width:35px;">
              <p class="g-pstyle1">55</p>
            </div>
            <div id="g-ai0-83" class="g-sm g-aiAbs g-aiPointText" style="top:64.4459%;margin-top:-10.6px;left:63.4264%;width:28px;">
              <p class="g-pstyle1">9</p>
            </div>
            <div id="g-ai0-84" class="g-sm g-aiAbs g-aiPointText" style="top:64.4226%;margin-top:-14.5px;left:46.2081%;width:40px;">
              <p class="g-pstyle0">MS</p>
            </div>
            <div id="g-ai0-85" class="g-sm g-aiAbs g-aiPointText" style="top:64.4226%;margin-top:-14.5px;left:50.844%;width:37px;">
              <p class="g-pstyle0">AL</p>
            </div>
            <div id="g-ai0-86" class="g-sm g-aiAbs g-aiPointText" style="top:64.4226%;margin-top:-14.5px;left:56.5353%;width:38px;">
              <p class="g-pstyle0">GA</p>
            </div>
            <div id="g-ai0-87" class="g-sm g-aiAbs g-aiPointText" style="top:64.9134%;margin-top:-14.5px;left:16.2401%;width:37px;">
              <p class="g-pstyle0">AZ</p>
            </div>
            <div id="g-ai0-88" class="g-sm g-aiAbs g-aiPointText" style="top:64.9367%;margin-top:-10.6px;left:21.3768%;width:28px;">
              <p class="g-pstyle1">5</p>
            </div>
            <div id="g-ai0-89" class="g-sm g-aiAbs g-aiPointText" style="top:66.1404%;margin-top:-14.5px;left:30.7037%;width:37px;">
              <p class="g-pstyle0">TX</p>
            </div>
            <div id="g-ai0-90" class="g-sm g-aiAbs g-aiPointText" style="top:66.9%;margin-top:-10.6px;left:47.3332%;width:28px;">
              <p class="g-pstyle1">6</p>
            </div>
            <div id="g-ai0-91" class="g-sm g-aiAbs g-aiPointText" style="top:66.9%;margin-top:-10.6px;left:51.6902%;width:28px;">
              <p class="g-pstyle1">9</p>
            </div>
            <div id="g-ai0-92" class="g-sm g-aiAbs g-aiPointText" style="top:66.9%;margin-top:-10.6px;left:57.001%;width:35px;">
              <p class="g-pstyle1">16</p>
            </div>
            <div id="g-ai0-93" class="g-sm g-aiAbs g-aiPointText" style="top:67.3908%;margin-top:-10.6px;left:16.5958%;width:35px;">
              <p class="g-pstyle1">11</p>
            </div>
            <div id="g-ai0-94" class="g-sm g-aiAbs g-aiPointText" style="top:67.8583%;margin-top:-14.5px;left:41.574%;width:37px;">
              <p class="g-pstyle0">LA</p>
            </div>
            <div id="g-ai0-95" class="g-sm g-aiAbs g-aiPointText" style="top:68.6178%;margin-top:-10.6px;left:31.0374%;width:35px;">
              <p class="g-pstyle1">38</p>
            </div>
            <div id="g-ai0-96" class="g-sm g-aiAbs g-aiPointText" style="top:70.3356%;margin-top:-10.6px;left:42.4119%;width:28px;">
              <p class="g-pstyle1">8</p>
            </div>
            <div id="g-ai0-97" class="g-sm g-aiAbs g-aiPointText" style="top:78.4107%;margin-top:-14.5px;left:59.851%;width:36px;">
              <p class="g-pstyle0">FL</p>
            </div>
            <div id="g-ai0-98" class="g-sm g-aiAbs g-aiPointText" style="top:81.1335%;margin-top:-10.6px;left:60.1089%;width:35px;">
              <p class="g-pstyle1">29</p>
            </div>
            <div id="g-ai0-99" class="g-sm g-aiAbs g-aiPointText" style="top:84.055%;margin-top:-14.5px;left:24.1482%;width:38px;">
              <p class="g-pstyle0">AK</p>
            </div>
            <div id="g-ai0-100" class="g-sm g-aiAbs g-aiPointText" style="top:86.5324%;margin-top:-10.6px;left:25.0808%;width:28px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai0-101" class="g-sm g-aiAbs g-aiPointText" style="top:92.3988%;margin-top:-14.5px;left:41.1016%;width:35px;">
              <p class="g-pstyle0">HI</p>
            </div>
            <div id="g-ai0-102" class="g-sm g-aiAbs g-aiPointText" style="top:94.8761%;margin-top:-10.6px;left:41.7951%;width:28px;">
              <p class="g-pstyle1">4</p>
            </div>
          </div>
          <div id="g-votesMap-md" class="g-artboard" style data-aspect-ratio="1.345" data-min-width="690">
            <div style="padding: 0 0 74.3718% 0;"></div>
            <div id="g-votesMap-md-img" class="g-aiImg" alt style="background-image: url(img/graphics/votesMap-md.png);"></div>
            <div id="g-ai1-1" class="g-states g-aiAbs g-aiPointText" style="top:7.826%;margin-top:-17.2px;left:90.6471%;width:42px;">
              <p class="g-pstyle0">ME</p>
            </div>
            <div id="g-ai1-2" class="g-states g-aiAbs g-aiPointText" style="top:10.4469%;margin-top:-12.6px;left:91.6301%;width:28px;">
              <p class="g-pstyle1">4</p>
            </div>
            <div id="g-ai1-3" class="g-states g-aiAbs g-aiPointText" style="top:11.9182%;margin-top:-17.2px;left:86.8257%;width:39px;">
              <p class="g-pstyle0">VT</p>
            </div>
            <div id="g-ai1-4" class="g-states g-aiAbs g-aiPointText" style="top:13.2823%;margin-top:-17.2px;left:83.3814%;width:42px;">
              <p class="g-pstyle0">NH</p>
            </div>
            <div id="g-ai1-5" class="g-states g-aiAbs g-aiPointText" style="top:14.3443%;margin-top:-12.6px;left:87.5986%;width:28px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai1-6" class="g-states g-aiAbs g-aiPointText" style="top:15.7084%;margin-top:-12.6px;left:84.3965%;width:28px;">
              <p class="g-pstyle1">4</p>
            </div>
            <div id="g-ai1-7" class="g-states g-aiAbs g-aiPointText" style="top:19.1284%;margin-top:-17.2px;left:85.7631%;width:42px;">
              <p class="g-pstyle0">MA</p>
            </div>
            <div id="g-ai1-8" class="g-states g-aiAbs g-aiPointText" style="top:21.5545%;margin-top:-12.6px;left:86.3208%;width:35px;">
              <p class="g-pstyle1">11</p>
            </div>
            <div id="g-ai1-9" class="g-states g-aiAbs g-aiPointText" style="top:24.7796%;margin-top:-17.2px;left:76.8502%;width:40px;">
              <p class="g-pstyle0">NY</p>
            </div>
            <div id="g-ai1-10" class="g-states g-aiAbs g-aiPointText" style="top:25.559%;margin-top:-17.2px;left:83.7602%;width:39px;">
              <p class="g-pstyle0">CT</p>
            </div>
            <div id="g-ai1-11" class="g-states g-aiAbs g-aiPointText" style="top:26.9231%;margin-top:-17.2px;left:89.0277%;width:36px;">
              <p class="g-pstyle0">RI</p>
            </div>
            <div id="g-ai1-12" class="g-states g-aiAbs g-aiPointText" style="top:27.2057%;margin-top:-12.6px;left:77.2503%;width:35px;">
              <p class="g-pstyle1">29</p>
            </div>
            <div id="g-ai1-13" class="g-states g-aiAbs g-aiPointText" style="top:28.18%;margin-top:-12.6px;left:84.5407%;width:28px;">
              <p class="g-pstyle1">7</p>
            </div>
            <div id="g-ai1-14" class="g-states g-aiAbs g-aiPointText" style="top:29.5441%;margin-top:-12.6px;left:89.5747%;width:28px;">
              <p class="g-pstyle1">4</p>
            </div>
            <div id="g-ai1-15" class="g-states g-aiAbs g-aiPointText" style="top:30.4308%;margin-top:-17.2px;left:53.7908%;width:38px;">
              <p class="g-pstyle0">MI</p>
            </div>
            <div id="g-ai1-16" class="g-states g-aiAbs g-aiPointText" style="top:33.4415%;margin-top:-12.6px;left:54.0423%;width:35px;">
              <p class="g-pstyle1">16</p>
            </div>
            <div id="g-ai1-17" class="g-states g-aiAbs g-aiPointText" style="top:33.9384%;margin-top:-17.2px;left:42.9819%;width:39px;">
              <p class="g-pstyle0">WI</p>
            </div>
            <div id="g-ai1-18" class="g-states g-aiAbs g-aiPointText" style="top:35.3025%;margin-top:-17.2px;left:71.7906%;width:39px;">
              <p class="g-pstyle0">PA</p>
            </div>
            <div id="g-ai1-19" class="g-states g-aiAbs g-aiPointText" style="top:36.2768%;margin-top:-17.2px;left:61.8557%;width:43px;">
              <p class="g-pstyle0">OH</p>
            </div>
            <div id="g-ai1-20" class="g-states g-aiAbs g-aiPointText" style="top:36.7543%;margin-top:-12.6px;left:43.2895%;width:35px;">
              <p class="g-pstyle1">10</p>
            </div>
            <div id="g-ai1-21" class="g-states g-aiAbs g-aiPointText" style="top:37.7286%;margin-top:-12.6px;left:72.1105%;width:35px;">
              <p class="g-pstyle1">20</p>
            </div>
            <div id="g-ai1-22" class="g-states g-aiAbs g-aiPointText" style="top:38.8978%;margin-top:-12.6px;left:62.4322%;width:35px;">
              <p class="g-pstyle1">18</p>
            </div>
            <div id="g-ai1-23" class="g-states g-aiAbs g-aiPointText" style="top:39.3947%;margin-top:-17.2px;left:11.4931%;width:43px;">
              <p class="g-pstyle0">WA</p>
            </div>
            <div id="g-ai1-24" class="g-states g-aiAbs g-aiPointText" style="top:39.5896%;margin-top:-17.2px;left:38.2664%;width:44px;">
              <p class="g-pstyle0">MN</p>
            </div>
            <div id="g-ai1-25" class="g-states g-aiAbs g-aiPointText" style="top:39.9793%;margin-top:-17.2px;left:53.686%;width:37px;">
              <p class="g-pstyle0">IN</p>
            </div>
            <div id="g-ai1-26" class="g-states g-aiAbs g-aiPointText" style="top:40.3691%;margin-top:-17.2px;left:79.2046%;width:40px;">
              <p class="g-pstyle0">NJ</p>
            </div>
            <div id="g-ai1-27" class="g-states g-aiAbs g-aiPointText" style="top:41.8208%;margin-top:-12.6px;left:12.107%;width:35px;">
              <p class="g-pstyle1">12</p>
            </div>
            <div id="g-ai1-28" class="g-states g-aiAbs g-aiPointText" style="top:42.3178%;margin-top:-17.2px;left:47.1419%;width:34px;">
              <p class="g-pstyle0">IL</p>
            </div>
            <div id="g-ai1-29" class="g-states g-aiAbs g-aiPointText" style="top:42.2106%;margin-top:-12.6px;left:38.9188%;width:35px;">
              <p class="g-pstyle1">10</p>
            </div>
            <div id="g-ai1-30" class="g-states g-aiAbs g-aiPointText" style="top:42.4055%;margin-top:-12.6px;left:53.8307%;width:35px;">
              <p class="g-pstyle1">11</p>
            </div>
            <div id="g-ai1-31" class="g-states g-aiAbs g-aiPointText" style="top:43.0973%;margin-top:-17.2px;left:59.0856%;width:43px;">
              <p class="g-pstyle0">WV</p>
            </div>
            <div id="g-ai1-32" class="g-states g-aiAbs g-aiPointText" style="top:43.1849%;margin-top:-12.6px;left:62.59%;width:28px;">
              <p class="g-pstyle1">5</p>
            </div>
            <div id="g-ai1-33" class="g-states g-aiAbs g-aiPointText" style="top:43.3798%;margin-top:-12.6px;left:79.7216%;width:35px;">
              <p class="g-pstyle1">14</p>
            </div>
            <div id="g-ai1-34" class="g-states g-aiAbs g-aiPointText" style="top:43.487%;margin-top:-17.2px;left:69.373%;width:43px;">
              <p class="g-pstyle0">MD</p>
            </div>
            <div id="g-ai1-35" class="g-states g-aiAbs g-aiPointText" style="top:44.7439%;margin-top:-12.6px;left:46.932%;width:35px;">
              <p class="g-pstyle1">20</p>
            </div>
            <div id="g-ai1-36" class="g-states g-aiAbs g-aiPointText" style="top:45.2408%;margin-top:-17.2px;left:74.3732%;width:40px;">
              <p class="g-pstyle0">DE</p>
            </div>
            <div id="g-ai1-37" class="g-states g-aiAbs g-aiPointText" style="top:45.9131%;margin-top:-12.6px;left:69.9922%;width:35px;">
              <p class="g-pstyle1">10</p>
            </div>
            <div id="g-ai1-38" class="g-states g-aiAbs g-aiPointText" style="top:46.2152%;margin-top:-17.2px;left:12.316%;width:42px;">
              <p class="g-pstyle0">OR</p>
            </div>
            <div id="g-ai1-39" class="g-states g-aiAbs g-aiPointText" style="top:47.5792%;margin-top:-17.2px;left:38.9664%;width:35px;">
              <p class="g-pstyle0">IA</p>
            </div>
            <div id="g-ai1-40" class="g-states g-aiAbs g-aiPointText" style="top:47.472%;margin-top:-12.6px;left:75.2164%;width:28px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai1-41" class="g-states g-aiAbs g-aiPointText" style="top:47.6669%;margin-top:-12.6px;left:41.9303%;width:28px;">
              <p class="g-pstyle1">6</p>
            </div>
            <div id="g-ai1-42" class="g-states g-aiAbs g-aiPointText" style="top:48.3587%;margin-top:-17.2px;left:57.6367%;width:39px;">
              <p class="g-pstyle0">KY</p>
            </div>
            <div id="g-ai1-43" class="g-states g-aiAbs g-aiPointText" style="top:48.3587%;margin-top:-17.2px;left:17.1346%;width:36px;">
              <p class="g-pstyle0">ID</p>
            </div>
            <div id="g-ai1-44" class="g-states g-aiAbs g-aiPointText" style="top:48.7485%;margin-top:-17.2px;left:67.6233%;width:39px;">
              <p class="g-pstyle0">VA</p>
            </div>
            <div id="g-ai1-45" class="g-states g-aiAbs g-aiPointText" style="top:48.7485%;margin-top:-17.2px;left:21.8141%;width:42px;">
              <p class="g-pstyle0">MT</p>
            </div>
            <div id="g-ai1-46" class="g-states g-aiAbs g-aiPointText" style="top:48.7485%;margin-top:-17.2px;left:27.5876%;width:42px;">
              <p class="g-pstyle0">ND</p>
            </div>
            <div id="g-ai1-47" class="g-states g-aiAbs g-aiPointText" style="top:49.031%;margin-top:-12.6px;left:25.351%;width:28px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai1-48" class="g-states g-aiAbs g-aiPointText" style="top:49.031%;margin-top:-12.6px;left:31.1481%;width:28px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai1-49" class="g-states g-aiAbs g-aiPointText" style="top:49.031%;margin-top:-12.6px;left:13.2849%;width:28px;">
              <p class="g-pstyle1">7</p>
            </div>
            <div id="g-ai1-50" class="g-states g-aiAbs g-aiPointText" style="top:50.7848%;margin-top:-12.6px;left:58.4117%;width:28px;">
              <p class="g-pstyle1">8</p>
            </div>
            <div id="g-ai1-51" class="g-states g-aiAbs g-aiPointText" style="top:50.9797%;margin-top:-12.6px;left:17.7058%;width:28px;">
              <p class="g-pstyle1">4</p>
            </div>
            <div id="g-ai1-52" class="g-states g-aiAbs g-aiPointText" style="top:51.1746%;margin-top:-12.6px;left:67.942%;width:35px;">
              <p class="g-pstyle1">13</p>
            </div>
            <div id="g-ai1-53" class="g-states g-aiAbs g-aiPointText" style="top:51.4766%;margin-top:-17.2px;left:22.0907%;width:42px;">
              <p class="g-pstyle0">WY</p>
            </div>
            <div id="g-ai1-54" class="g-states g-aiAbs g-aiPointText" style="top:51.4766%;margin-top:-17.2px;left:27.5876%;width:40px;">
              <p class="g-pstyle0">SD</p>
            </div>
            <div id="g-ai1-55" class="g-states g-aiAbs g-aiPointText" style="top:51.5643%;margin-top:-12.6px;left:25.4905%;width:28px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai1-56" class="g-states g-aiAbs g-aiPointText" style="top:51.5643%;margin-top:-12.6px;left:31.2876%;width:28px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai1-57" class="g-states g-aiAbs g-aiPointText" style="top:51.6715%;margin-top:-17.2px;left:34.28%;width:40px;">
              <p class="g-pstyle0">NE</p>
            </div>
            <div id="g-ai1-58" class="g-states g-aiAbs g-aiPointText" style="top:52.8407%;margin-top:-17.2px;left:38.9664%;width:39px;">
              <p class="g-pstyle0">KS</p>
            </div>
            <div id="g-ai1-59" class="g-states g-aiAbs g-aiPointText" style="top:53.1232%;margin-top:-12.6px;left:41.9303%;width:28px;">
              <p class="g-pstyle1">6</p>
            </div>
            <div id="g-ai1-60" class="g-states g-aiAbs g-aiPointText" style="top:53.815%;margin-top:-17.2px;left:52.9488%;width:41px;">
              <p class="g-pstyle0">TN</p>
            </div>
            <div id="g-ai1-61" class="g-states g-aiAbs g-aiPointText" style="top:54.4873%;margin-top:-12.6px;left:35.1564%;width:28px;">
              <p class="g-pstyle1">5</p>
            </div>
            <div id="g-ai1-62" class="g-states g-aiAbs g-aiPointText" style="top:54.5945%;margin-top:-17.2px;left:46.0493%;width:44px;">
              <p class="g-pstyle0">MO</p>
            </div>
            <div id="g-ai1-63" class="g-states g-aiAbs g-aiPointText" style="top:55.374%;margin-top:-17.2px;left:64.0322%;width:41px;">
              <p class="g-pstyle0">NC</p>
            </div>
            <div id="g-ai1-64" class="g-states g-aiAbs g-aiPointText" style="top:56.5432%;margin-top:-17.2px;left:17.836%;width:41px;">
              <p class="g-pstyle0">NV</p>
            </div>
            <div id="g-ai1-65" class="g-states g-aiAbs g-aiPointText" style="top:56.5432%;margin-top:-17.2px;left:21.952%;width:41px;">
              <p class="g-pstyle0">UT</p>
            </div>
            <div id="g-ai1-66" class="g-states g-aiAbs g-aiPointText" style="top:56.5432%;margin-top:-17.2px;left:27.0784%;width:41px;">
              <p class="g-pstyle0">CO</p>
            </div>
            <div id="g-ai1-67" class="g-states g-aiAbs g-aiPointText" style="top:56.8258%;margin-top:-12.6px;left:53.5201%;width:35px;">
              <p class="g-pstyle1">11</p>
            </div>
            <div id="g-ai1-68" class="g-states g-aiAbs g-aiPointText" style="top:57.0206%;margin-top:-12.6px;left:46.7046%;width:35px;">
              <p class="g-pstyle1">10</p>
            </div>
            <div id="g-ai1-69" class="g-states g-aiAbs g-aiPointText" style="top:57.3227%;margin-top:-17.2px;left:78.3224%;width:40px;">
              <p class="g-pstyle0">DC</p>
            </div>
            <div id="g-ai1-70" class="g-states g-aiAbs g-aiPointText" style="top:57.3227%;margin-top:-17.2px;left:36.8354%;width:42px;">
              <p class="g-pstyle0">OK</p>
            </div>
            <div id="g-ai1-71" class="g-states g-aiAbs g-aiPointText" style="top:57.995%;margin-top:-12.6px;left:64.4864%;width:35px;">
              <p class="g-pstyle1">15</p>
            </div>
            <div id="g-ai1-72" class="g-states g-aiAbs g-aiPointText" style="top:58.7744%;margin-top:-12.6px;left:18.729%;width:28px;">
              <p class="g-pstyle1">6</p>
            </div>
            <div id="g-ai1-73" class="g-states g-aiAbs g-aiPointText" style="top:58.7744%;margin-top:-12.6px;left:22.8371%;width:28px;">
              <p class="g-pstyle1">6</p>
            </div>
            <div id="g-ai1-74" class="g-states g-aiAbs g-aiPointText" style="top:58.7744%;margin-top:-12.6px;left:27.9724%;width:28px;">
              <p class="g-pstyle1">9</p>
            </div>
            <div id="g-ai1-75" class="g-states g-aiAbs g-aiPointText" style="top:59.2714%;margin-top:-17.2px;left:42.5249%;width:40px;">
              <p class="g-pstyle0">AR</p>
            </div>
            <div id="g-ai1-76" class="g-states g-aiAbs g-aiPointText" style="top:59.5539%;margin-top:-12.6px;left:37.8119%;width:28px;">
              <p class="g-pstyle1">7</p>
            </div>
            <div id="g-ai1-77" class="g-states g-aiAbs g-aiPointText" style="top:59.7488%;margin-top:-12.6px;left:79.2032%;width:28px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai1-78" class="g-states g-aiAbs g-aiPointText" style="top:61.2201%;margin-top:-17.2px;left:10.55%;width:40px;">
              <p class="g-pstyle0">CA</p>
            </div>
            <div id="g-ai1-79" class="g-states g-aiAbs g-aiPointText" style="top:61.8924%;margin-top:-12.6px;left:43.3781%;width:28px;">
              <p class="g-pstyle1">6</p>
            </div>
            <div id="g-ai1-80" class="g-states g-aiAbs g-aiPointText" style="top:61.9995%;margin-top:-17.2px;left:61.1935%;width:39px;">
              <p class="g-pstyle0">SC</p>
            </div>
            <div id="g-ai1-81" class="g-states g-aiAbs g-aiPointText" style="top:62.9739%;margin-top:-17.2px;left:23.6386%;width:44px;">
              <p class="g-pstyle0">NM</p>
            </div>
            <div id="g-ai1-82" class="g-states g-aiAbs g-aiPointText" style="top:64.0359%;margin-top:-12.6px;left:10.9093%;width:35px;">
              <p class="g-pstyle1">55</p>
            </div>
            <div id="g-ai1-83" class="g-states g-aiAbs g-aiPointText" style="top:64.338%;margin-top:-17.2px;left:46.7397%;width:42px;">
              <p class="g-pstyle0">MS</p>
            </div>
            <div id="g-ai1-84" class="g-states g-aiAbs g-aiPointText" style="top:64.338%;margin-top:-17.2px;left:50.8458%;width:39px;">
              <p class="g-pstyle0">AL</p>
            </div>
            <div id="g-ai1-85" class="g-states g-aiAbs g-aiPointText" style="top:64.338%;margin-top:-17.2px;left:55.885%;width:40px;">
              <p class="g-pstyle0">GA</p>
            </div>
            <div id="g-ai1-86" class="g-states g-aiAbs g-aiPointText" style="top:64.4256%;margin-top:-12.6px;left:61.9872%;width:28px;">
              <p class="g-pstyle1">9</p>
            </div>
            <div id="g-ai1-87" class="g-states g-aiAbs g-aiPointText" style="top:64.9226%;margin-top:-17.2px;left:20.2024%;width:39px;">
              <p class="g-pstyle0">AZ</p>
            </div>
            <div id="g-ai1-88" class="g-states g-aiAbs g-aiPointText" style="top:65.0103%;margin-top:-12.6px;left:24.7506%;width:28px;">
              <p class="g-pstyle1">5</p>
            </div>
            <div id="g-ai1-89" class="g-states g-aiAbs g-aiPointText" style="top:66.0918%;margin-top:-17.2px;left:33.0101%;width:39px;">
              <p class="g-pstyle0">TX</p>
            </div>
            <div id="g-ai1-90" class="g-states g-aiAbs g-aiPointText" style="top:66.9589%;margin-top:-12.6px;left:47.7361%;width:28px;">
              <p class="g-pstyle1">6</p>
            </div>
            <div id="g-ai1-91" class="g-states g-aiAbs g-aiPointText" style="top:66.9589%;margin-top:-12.6px;left:51.5943%;width:28px;">
              <p class="g-pstyle1">9</p>
            </div>
            <div id="g-ai1-92" class="g-states g-aiAbs g-aiPointText" style="top:66.9589%;margin-top:-12.6px;left:56.2973%;width:35px;">
              <p class="g-pstyle1">16</p>
            </div>
            <div id="g-ai1-93" class="g-states g-aiAbs g-aiPointText" style="top:67.3487%;margin-top:-12.6px;left:20.5167%;width:35px;">
              <p class="g-pstyle1">11</p>
            </div>
            <div id="g-ai1-94" class="g-states g-aiAbs g-aiPointText" style="top:67.6507%;margin-top:-17.2px;left:42.636%;width:39px;">
              <p class="g-pstyle0">LA</p>
            </div>
            <div id="g-ai1-95" class="g-states g-aiAbs g-aiPointText" style="top:68.5179%;margin-top:-12.6px;left:33.3054%;width:35px;">
              <p class="g-pstyle1">38</p>
            </div>
            <div id="g-ai1-96" class="g-states g-aiAbs g-aiPointText" style="top:70.0768%;margin-top:-12.6px;left:43.3781%;width:28px;">
              <p class="g-pstyle1">8</p>
            </div>
            <div id="g-ai1-97" class="g-states g-aiAbs g-aiPointText" style="top:77.7839%;margin-top:-17.2px;left:58.8212%;width:38px;">
              <p class="g-pstyle0">FL</p>
            </div>
            <div id="g-ai1-98" class="g-states g-aiAbs g-aiPointText" style="top:80.4049%;margin-top:-12.6px;left:59.0495%;width:35px;">
              <p class="g-pstyle1">29</p>
            </div>
            <div id="g-ai1-99" class="g-states g-aiAbs g-aiPointText" style="top:83.0454%;margin-top:-17.2px;left:27.2053%;width:40px;">
              <p class="g-pstyle0">AK</p>
            </div>
            <div id="g-ai1-100" class="g-states g-aiAbs g-aiPointText" style="top:85.6664%;margin-top:-12.6px;left:28.0306%;width:28px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai1-101" class="g-states g-aiAbs g-aiPointText" style="top:90.8401%;margin-top:-17.2px;left:42.2178%;width:37px;">
              <p class="g-pstyle0">HI</p>
            </div>
            <div id="g-ai1-102" class="g-states g-aiAbs g-aiPointText" style="top:93.4611%;margin-top:-12.6px;left:42.8319%;width:28px;">
              <p class="g-pstyle1">4</p>
            </div>
          </div>
          <div id="g-votesMap-xs" class="g-artboard" style="max-width: 509px;max-height: 501px" data-aspect-ratio="1.015" data-min-width="0" data-max-width="509">
            <div style="padding: 0 0 98.5084% 0;"></div>
            <div id="g-votesMap-xs-img" class="g-aiImg" alt style="background-image: url(img/graphics/votesMap-xs.png);"></div>
            <div id="g-ai2-1" class="g-xs_states g-aiAbs g-aiPointText" style="top:5.1896%;margin-top:-8.9px;left:89.0915%;width:35px;">
              <p class="g-pstyle0">VT</p>
            </div>
            <div id="g-ai2-2" class="g-xs_states g-aiAbs g-aiPointText" style="top:5.1926%;margin-top:-6.9px;left:93.3005%;width:27px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai2-3" class="g-xs_states g-aiAbs g-aiPointText" style="top:6.42%;margin-top:-8.9px;left:94.782%;width:37px;">
              <p class="g-pstyle0">ME</p>
            </div>
            <div id="g-ai2-4" class="g-xs_states g-aiAbs g-aiPointText" style="top:9.8068%;margin-top:-6.9px;left:95.8354%;width:27px;">
              <p class="g-pstyle1">4</p>
            </div>
            <div id="g-ai2-5" class="g-xs_states g-aiAbs g-aiPointText" style="top:10.1115%;margin-top:-8.9px;left:87.8625%;width:37px;">
              <p class="g-pstyle0">NH</p>
            </div>
            <div id="g-ai2-6" class="g-xs_states g-aiAbs g-aiPointText" style="top:13.4983%;margin-top:-6.9px;left:88.9499%;width:27px;">
              <p class="g-pstyle1">4</p>
            </div>
            <div id="g-ai2-7" class="g-xs_states g-aiAbs g-aiPointText" style="top:18.1095%;margin-top:-8.9px;left:88.1868%;width:37px;">
              <p class="g-pstyle0">MA</p>
            </div>
            <div id="g-ai2-8" class="g-xs_states g-aiAbs g-aiPointText" style="top:18.1125%;margin-top:-6.9px;left:93.7284%;width:33px;">
              <p class="g-pstyle1">11</p>
            </div>
            <div id="g-ai2-9" class="g-xs_states g-aiAbs g-aiPointText" style="top:22.7237%;margin-top:-8.9px;left:79.0181%;width:36px;">
              <p class="g-pstyle0">NY</p>
            </div>
            <div id="g-ai2-10" class="g-xs_states g-aiAbs g-aiPointText" style="top:24.2619%;margin-top:-8.9px;left:87.9881%;width:35px;">
              <p class="g-pstyle0">CT</p>
            </div>
            <div id="g-ai2-11" class="g-xs_states g-aiAbs g-aiPointText" style="top:25.4924%;margin-top:-8.9px;left:95.6674%;width:32px;">
              <p class="g-pstyle0">RI</p>
            </div>
            <div id="g-ai2-12" class="g-xs_states g-aiAbs g-aiPointText" style="top:26.1106%;margin-top:-6.9px;left:79.4466%;width:33px;">
              <p class="g-pstyle1">29</p>
            </div>
            <div id="g-ai2-13" class="g-xs_states g-aiAbs g-aiPointText" style="top:27.6487%;margin-top:-6.9px;left:88.8243%;width:27px;">
              <p class="g-pstyle1">7</p>
            </div>
            <div id="g-ai2-14" class="g-xs_states g-aiAbs g-aiPointText" style="top:28.8762%;margin-top:-8.9px;left:54.0108%;width:34px;">
              <p class="g-pstyle0">MI</p>
            </div>
            <div id="g-ai2-15" class="g-xs_states g-aiAbs g-aiPointText" style="top:29.1868%;margin-top:-6.9px;left:96.2534%;width:27px;">
              <p class="g-pstyle1">4</p>
            </div>
            <div id="g-ai2-16" class="g-xs_states g-aiAbs g-aiPointText" style="top:30.4143%;margin-top:-8.9px;left:42.431%;width:35px;">
              <p class="g-pstyle0">WI</p>
            </div>
            <div id="g-ai2-17" class="g-xs_states g-aiAbs g-aiPointText" style="top:32.263%;margin-top:-6.9px;left:54.2803%;width:33px;">
              <p class="g-pstyle1">16</p>
            </div>
            <div id="g-ai2-18" class="g-xs_states g-aiAbs g-aiPointText" style="top:33.8011%;margin-top:-6.9px;left:42.7605%;width:33px;">
              <p class="g-pstyle1">10</p>
            </div>
            <div id="g-ai2-19" class="g-xs_states g-aiAbs g-aiPointText" style="top:34.4133%;margin-top:-8.9px;left:74.5068%;width:35px;">
              <p class="g-pstyle0">PA</p>
            </div>
            <div id="g-ai2-20" class="g-xs_states g-aiAbs g-aiPointText" style="top:35.0286%;margin-top:-8.9px;left:62.9541%;width:37px;">
              <p class="g-pstyle0">OH</p>
            </div>
            <div id="g-ai2-21" class="g-xs_states g-aiAbs g-aiPointText" style="top:37.8001%;margin-top:-6.9px;left:74.8494%;width:33px;">
              <p class="g-pstyle1">20</p>
            </div>
            <div id="g-ai2-22" class="g-xs_states g-aiAbs g-aiPointText" style="top:37.7971%;margin-top:-8.9px;left:5.3628%;width:38px;">
              <p class="g-pstyle0">WA</p>
            </div>
            <div id="g-ai2-23" class="g-xs_states g-aiAbs g-aiPointText" style="top:38.1047%;margin-top:-8.9px;left:36.1668%;width:38px;">
              <p class="g-pstyle0">MN</p>
            </div>
            <div id="g-ai2-24" class="g-xs_states g-aiAbs g-aiPointText" style="top:38.4154%;margin-top:-6.9px;left:63.5717%;width:33px;">
              <p class="g-pstyle1">18</p>
            </div>
            <div id="g-ai2-25" class="g-xs_states g-aiAbs g-aiPointText" style="top:38.4124%;margin-top:-8.9px;left:53.8985%;width:33px;">
              <p class="g-pstyle0">IN</p>
            </div>
            <div id="g-ai2-26" class="g-xs_states g-aiAbs g-aiPointText" style="top:38.4123%;margin-top:-8.9px;left:83.3586%;width:35px;">
              <p class="g-pstyle0">NJ</p>
            </div>
            <div id="g-ai2-27" class="g-xs_states g-aiAbs g-aiPointText" style="top:41.1809%;margin-top:-8.9px;left:46.5845%;width:31px;">
              <p class="g-pstyle0">IL</p>
            </div>
            <div id="g-ai2-28" class="g-xs_states g-aiAbs g-aiPointText" style="top:41.4916%;margin-top:-6.9px;left:6.0203%;width:33px;">
              <p class="g-pstyle1">12</p>
            </div>
            <div id="g-ai2-29" class="g-xs_states g-aiAbs g-aiPointText" style="top:41.4916%;margin-top:-6.9px;left:36.866%;width:33px;">
              <p class="g-pstyle1">10</p>
            </div>
            <div id="g-ai2-30" class="g-xs_states g-aiAbs g-aiPointText" style="top:41.7992%;margin-top:-6.9px;left:54.0535%;width:33px;">
              <p class="g-pstyle1">11</p>
            </div>
            <div id="g-ai2-31" class="g-xs_states g-aiAbs g-aiPointText" style="top:41.7992%;margin-top:-6.9px;left:83.9124%;width:33px;">
              <p class="g-pstyle1">14</p>
            </div>
            <div id="g-ai2-32" class="g-xs_states g-aiAbs g-aiPointText" style="top:42.1037%;margin-top:-8.9px;left:59.0771%;width:38px;">
              <p class="g-pstyle0">WV</p>
            </div>
            <div id="g-ai2-33" class="g-xs_states g-aiAbs g-aiPointText" style="top:42.4144%;margin-top:-6.9px;left:64.7396%;width:27px;">
              <p class="g-pstyle1">5</p>
            </div>
            <div id="g-ai2-34" class="g-xs_states g-aiAbs g-aiPointText" style="top:43.9494%;margin-top:-8.9px;left:69.1891%;width:38px;">
              <p class="g-pstyle0">MD</p>
            </div>
            <div id="g-ai2-35" class="g-xs_states g-aiAbs g-aiPointText" style="top:43.9525%;margin-top:-6.9px;left:74.748%;width:33px;">
              <p class="g-pstyle1">10</p>
            </div>
            <div id="g-ai2-36" class="g-xs_states g-aiAbs g-aiPointText" style="top:44.2571%;margin-top:-8.9px;left:77.8795%;width:35px;">
              <p class="g-pstyle0">DE</p>
            </div>
            <div id="g-ai2-37" class="g-xs_states g-aiAbs g-aiPointText" style="top:44.5677%;margin-top:-6.9px;left:46.3598%;width:33px;">
              <p class="g-pstyle1">20</p>
            </div>
            <div id="g-ai2-38" class="g-xs_states g-aiAbs g-aiPointText" style="top:47.0257%;margin-top:-8.9px;left:13.2249%;width:33px;">
              <p class="g-pstyle0">ID</p>
            </div>
            <div id="g-ai2-39" class="g-xs_states g-aiAbs g-aiPointText" style="top:47.3363%;margin-top:-6.9px;left:40.563%;width:27px;">
              <p class="g-pstyle1">6</p>
            </div>
            <div id="g-ai2-40" class="g-xs_states g-aiAbs g-aiPointText" style="top:47.3332%;margin-top:-8.9px;left:36.614%;width:32px;">
              <p class="g-pstyle0">IA</p>
            </div>
            <div id="g-ai2-41" class="g-xs_states g-aiAbs g-aiPointText" style="top:47.6439%;margin-top:-6.9px;left:21.8819%;width:27px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai2-42" class="g-xs_states g-aiAbs g-aiPointText" style="top:47.6439%;margin-top:-6.9px;left:28.9142%;width:27px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai2-43" class="g-xs_states g-aiAbs g-aiPointText" style="top:47.6409%;margin-top:-8.9px;left:4.722%;width:37px;">
              <p class="g-pstyle0">OR</p>
            </div>
            <div id="g-ai2-44" class="g-xs_states g-aiAbs g-aiPointText" style="top:47.6439%;margin-top:-6.9px;left:10.0096%;width:27px;">
              <p class="g-pstyle1">7</p>
            </div>
            <div id="g-ai2-45" class="g-xs_states g-aiAbs g-aiPointText" style="top:47.6409%;margin-top:-8.9px;left:17.3289%;width:37px;">
              <p class="g-pstyle0">MT</p>
            </div>
            <div id="g-ai2-46" class="g-xs_states g-aiAbs g-aiPointText" style="top:47.6409%;margin-top:-8.9px;left:24.4234%;width:37px;">
              <p class="g-pstyle0">ND</p>
            </div>
            <div id="g-ai2-47" class="g-xs_states g-aiAbs g-aiPointText" style="top:47.9516%;margin-top:-6.9px;left:78.783%;width:27px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai2-48" class="g-xs_states g-aiAbs g-aiPointText" style="top:47.9516%;margin-top:-6.9px;left:61.3974%;width:27px;">
              <p class="g-pstyle1">8</p>
            </div>
            <div id="g-ai2-49" class="g-xs_states g-aiAbs g-aiPointText" style="top:47.9485%;margin-top:-8.9px;left:56.6157%;width:35px;">
              <p class="g-pstyle0">KY</p>
            </div>
            <div id="g-ai2-50" class="g-xs_states g-aiAbs g-aiPointText" style="top:49.4866%;margin-top:-8.9px;left:65.7728%;width:35px;">
              <p class="g-pstyle0">VA</p>
            </div>
            <div id="g-ai2-51" class="g-xs_states g-aiAbs g-aiPointText" style="top:49.7973%;margin-top:-6.9px;left:70.5648%;width:33px;">
              <p class="g-pstyle1">13</p>
            </div>
            <div id="g-ai2-52" class="g-xs_states g-aiAbs g-aiPointText" style="top:50.4125%;margin-top:-6.9px;left:13.8367%;width:27px;">
              <p class="g-pstyle1">4</p>
            </div>
            <div id="g-ai2-53" class="g-xs_states g-aiAbs g-aiPointText" style="top:50.4094%;margin-top:-8.9px;left:31.5933%;width:36px;">
              <p class="g-pstyle0">NE</p>
            </div>
            <div id="g-ai2-54" class="g-xs_states g-aiAbs g-aiPointText" style="top:51.0277%;margin-top:-6.9px;left:22.5781%;width:27px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai2-55" class="g-xs_states g-aiAbs g-aiPointText" style="top:51.0277%;margin-top:-6.9px;left:28.5377%;width:27px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai2-56" class="g-xs_states g-aiAbs g-aiPointText" style="top:51.0247%;margin-top:-8.9px;left:17.9283%;width:37px;">
              <p class="g-pstyle0">WY</p>
            </div>
            <div id="g-ai2-57" class="g-xs_states g-aiAbs g-aiPointText" style="top:51.0247%;margin-top:-8.9px;left:24.4234%;width:36px;">
              <p class="g-pstyle0">SD</p>
            </div>
            <div id="g-ai2-58" class="g-xs_states g-aiAbs g-aiPointText" style="top:52.5658%;margin-top:-6.9px;left:41.1927%;width:27px;">
              <p class="g-pstyle1">6</p>
            </div>
            <div id="g-ai2-59" class="g-xs_states g-aiAbs g-aiPointText" style="top:52.5628%;margin-top:-8.9px;left:36.3109%;width:35px;">
              <p class="g-pstyle0">KS</p>
            </div>
            <div id="g-ai2-60" class="g-xs_states g-aiAbs g-aiPointText" style="top:52.5628%;margin-top:-8.9px;left:53.1087%;width:36px;">
              <p class="g-pstyle0">TN</p>
            </div>
            <div id="g-ai2-61" class="g-xs_states g-aiAbs g-aiPointText" style="top:52.8704%;margin-top:-8.9px;left:44.2019%;width:38px;">
              <p class="g-pstyle0">MO</p>
            </div>
            <div id="g-ai2-62" class="g-xs_states g-aiAbs g-aiPointText" style="top:54.1039%;margin-top:-6.9px;left:32.5321%;width:27px;">
              <p class="g-pstyle1">5</p>
            </div>
            <div id="g-ai2-63" class="g-xs_states g-aiAbs g-aiPointText" style="top:54.7161%;margin-top:-8.9px;left:66.1949%;width:36px;">
              <p class="g-pstyle0">NC</p>
            </div>
            <div id="g-ai2-64" class="g-xs_states g-aiAbs g-aiPointText" style="top:55.3314%;margin-top:-8.9px;left:12.4611%;width:36px;">
              <p class="g-pstyle0">NV</p>
            </div>
            <div id="g-ai2-65" class="g-xs_states g-aiAbs g-aiPointText" style="top:55.3314%;margin-top:-8.9px;left:17.1737%;width:36px;">
              <p class="g-pstyle0">UT</p>
            </div>
            <div id="g-ai2-66" class="g-xs_states g-aiAbs g-aiPointText" style="top:55.3314%;margin-top:-8.9px;left:23.2719%;width:36px;">
              <p class="g-pstyle0">CO</p>
            </div>
            <div id="g-ai2-67" class="g-xs_states g-aiAbs g-aiPointText" style="top:55.9496%;margin-top:-6.9px;left:53.7208%;width:33px;">
              <p class="g-pstyle1">11</p>
            </div>
            <div id="g-ai2-68" class="g-xs_states g-aiAbs g-aiPointText" style="top:56.2573%;margin-top:-6.9px;left:44.904%;width:33px;">
              <p class="g-pstyle1">10</p>
            </div>
            <div id="g-ai2-69" class="g-xs_states g-aiAbs g-aiPointText" style="top:56.8694%;margin-top:-8.9px;left:80.8983%;width:36px;">
              <p class="g-pstyle0">DC</p>
            </div>
            <div id="g-ai2-70" class="g-xs_states g-aiAbs g-aiPointText" style="top:56.8695%;margin-top:-8.9px;left:34.3307%;width:37px;">
              <p class="g-pstyle0">OK</p>
            </div>
            <div id="g-ai2-71" class="g-xs_states g-aiAbs g-aiPointText" style="top:58.103%;margin-top:-6.9px;left:66.6814%;width:33px;">
              <p class="g-pstyle1">15</p>
            </div>
            <div id="g-ai2-72" class="g-xs_states g-aiAbs g-aiPointText" style="top:58.1%;margin-top:-8.9px;left:4.3525%;width:35px;">
              <p class="g-pstyle0">CA</p>
            </div>
            <div id="g-ai2-73" class="g-xs_states g-aiAbs g-aiPointText" style="top:58.4106%;margin-top:-6.9px;left:13.4178%;width:27px;">
              <p class="g-pstyle1">6</p>
            </div>
            <div id="g-ai2-74" class="g-xs_states g-aiAbs g-aiPointText" style="top:58.4106%;margin-top:-6.9px;left:18.122%;width:27px;">
              <p class="g-pstyle1">6</p>
            </div>
            <div id="g-ai2-75" class="g-xs_states g-aiAbs g-aiPointText" style="top:58.4106%;margin-top:-6.9px;left:24.2297%;width:27px;">
              <p class="g-pstyle1">9</p>
            </div>
            <div id="g-ai2-76" class="g-xs_states g-aiAbs g-aiPointText" style="top:58.4075%;margin-top:-8.9px;left:41.0255%;width:36px;">
              <p class="g-pstyle0">AR</p>
            </div>
            <div id="g-ai2-77" class="g-xs_states g-aiAbs g-aiPointText" style="top:60.2563%;margin-top:-6.9px;left:81.842%;width:27px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai2-78" class="g-xs_states g-aiAbs g-aiPointText" style="top:60.2563%;margin-top:-6.9px;left:35.3771%;width:27px;">
              <p class="g-pstyle1">7</p>
            </div>
            <div id="g-ai2-79" class="g-xs_states g-aiAbs g-aiPointText" style="top:61.4868%;margin-top:-6.9px;left:4.7373%;width:33px;">
              <p class="g-pstyle1">55</p>
            </div>
            <div id="g-ai2-80" class="g-xs_states g-aiAbs g-aiPointText" style="top:61.7944%;margin-top:-6.9px;left:41.9462%;width:27px;">
              <p class="g-pstyle1">6</p>
            </div>
            <div id="g-ai2-81" class="g-xs_states g-aiAbs g-aiPointText" style="top:62.0989%;margin-top:-8.9px;left:62.6349%;width:35px;">
              <p class="g-pstyle0">SC</p>
            </div>
            <div id="g-ai2-82" class="g-xs_states g-aiAbs g-aiPointText" style="top:62.099%;margin-top:-8.9px;left:50.5463%;width:35px;">
              <p class="g-pstyle0">AL</p>
            </div>
            <div id="g-ai2-83" class="g-xs_states g-aiAbs g-aiPointText" style="top:63.6371%;margin-top:-8.9px;left:45.2447%;width:37px;">
              <p class="g-pstyle0">MS</p>
            </div>
            <div id="g-ai2-84" class="g-xs_states g-aiAbs g-aiPointText" style="top:63.9447%;margin-top:-8.9px;left:56.661%;width:36px;">
              <p class="g-pstyle0">GA</p>
            </div>
            <div id="g-ai2-85" class="g-xs_states g-aiAbs g-aiPointText" style="top:63.9447%;margin-top:-8.9px;left:19.2837%;width:38px;">
              <p class="g-pstyle0">NM</p>
            </div>
            <div id="g-ai2-86" class="g-xs_states g-aiAbs g-aiPointText" style="top:64.2523%;margin-top:-8.9px;left:14.0807%;width:35px;">
              <p class="g-pstyle0">AZ</p>
            </div>
            <div id="g-ai2-87" class="g-xs_states g-aiAbs g-aiPointText" style="top:65.1751%;margin-top:-8.9px;left:30.2327%;width:35px;">
              <p class="g-pstyle0">TX</p>
            </div>
            <div id="g-ai2-88" class="g-xs_states g-aiAbs g-aiPointText" style="top:65.4858%;margin-top:-6.9px;left:63.4853%;width:27px;">
              <p class="g-pstyle1">9</p>
            </div>
            <div id="g-ai2-89" class="g-xs_states g-aiAbs g-aiPointText" style="top:65.4858%;margin-top:-6.9px;left:51.3547%;width:27px;">
              <p class="g-pstyle1">9</p>
            </div>
            <div id="g-ai2-90" class="g-xs_states g-aiAbs g-aiPointText" style="top:66.4057%;margin-top:-8.9px;left:41.1513%;width:34px;">
              <p class="g-pstyle0">LA</p>
            </div>
            <div id="g-ai2-91" class="g-xs_states g-aiAbs g-aiPointText" style="top:67.0239%;margin-top:-6.9px;left:20.4749%;width:27px;">
              <p class="g-pstyle1">5</p>
            </div>
            <div id="g-ai2-92" class="g-xs_states g-aiAbs g-aiPointText" style="top:67.0239%;margin-top:-6.9px;left:46.312%;width:27px;">
              <p class="g-pstyle1">6</p>
            </div>
            <div id="g-ai2-93" class="g-xs_states g-aiAbs g-aiPointText" style="top:67.0239%;margin-top:-6.9px;left:14.424%;width:33px;">
              <p class="g-pstyle1">11</p>
            </div>
            <div id="g-ai2-94" class="g-xs_states g-aiAbs g-aiPointText" style="top:67.3315%;margin-top:-6.9px;left:57.1027%;width:33px;">
              <p class="g-pstyle1">16</p>
            </div>
            <div id="g-ai2-95" class="g-xs_states g-aiAbs g-aiPointText" style="top:67.9468%;margin-top:-6.9px;left:30.5492%;width:33px;">
              <p class="g-pstyle1">38</p>
            </div>
            <div id="g-ai2-96" class="g-xs_states g-aiAbs g-aiPointText" style="top:70.1001%;margin-top:-6.9px;left:41.9464%;width:27px;">
              <p class="g-pstyle1">8</p>
            </div>
            <div id="g-ai2-97" class="g-xs_states g-aiAbs g-aiPointText" style="top:76.8647%;margin-top:-8.9px;left:59.703%;width:34px;">
              <p class="g-pstyle0">FL</p>
            </div>
            <div id="g-ai2-98" class="g-xs_states g-aiAbs g-aiPointText" style="top:80.5591%;margin-top:-6.9px;left:59.9476%;width:33px;">
              <p class="g-pstyle1">29</p>
            </div>
            <div id="g-ai2-99" class="g-xs_states g-aiAbs g-aiPointText" style="top:83.3247%;margin-top:-8.9px;left:24.0073%;width:36px;">
              <p class="g-pstyle0">AK</p>
            </div>
            <div id="g-ai2-100" class="g-xs_states g-aiAbs g-aiPointText" style="top:86.0963%;margin-top:-6.9px;left:24.898%;width:27px;">
              <p class="g-pstyle1">3</p>
            </div>
            <div id="g-ai2-101" class="g-xs_states g-aiAbs g-aiPointText" style="top:89.1694%;margin-top:-8.9px;left:39.0648%;width:33px;">
              <p class="g-pstyle0">HI</p>
            </div>
            <div id="g-ai2-102" class="g-xs_states g-aiAbs g-aiPointText" style="top:92.5563%;margin-top:-6.9px;left:39.7228%;width:27px;">
              <p class="g-pstyle1">4</p>
            </div>
          </div>
        </div>
        <component is="script">
          (function(containerId, opts) {
            if (!("querySelector" in document)) return;
            var container = document.getElementById(containerId);
            var nameSpace = opts.namespace || "";
            var onResize = throttle(update, 200);
            var waiting = !!window.IntersectionObserver;
            var observer;
            update();

            document.addEventListener("DOMContentLoaded", update);
            window.addEventListener("resize", onResize);

            // NYT Scoop-specific code
            if (opts.setup) {
              opts.setup(container).on("cleanup", cleanup);
            }

            function cleanup() {
              document.removeEventListener("DOMContentLoaded", update);
              window.removeEventListener("resize", onResize);
              if (observer) observer.disconnect();
            }

            function update() {
              var artboards = selectChildren(
                  "." + nameSpace + "artboard[data-min-width]",
                  container
                ),
                width = Math.round(container.getBoundingClientRect().width);

              // Set artboard visibility based on container width
              artboards.forEach(function(el) {
                var minwidth = el.getAttribute("data-min-width"),
                  maxwidth = el.getAttribute("data-max-width");
                if (+minwidth <= width && (+maxwidth >= width || maxwidth === null)) {
                  if (!waiting) {
                    selectChildren("." + nameSpace + "aiImg", el).forEach(
                      updateImgSrc
                    );
                  }
                  el.style.display = "block";
                } else {
                  el.style.display = "none";
                }
              });

              // Initialize lazy loading on first call
              if (waiting && !observer) {
                if (elementInView(container)) {
                  waiting = false;
                  update();
                } else {
                  observer = new IntersectionObserver(onIntersectionChange, {});
                  observer.observe(container);
                }
              }
            }

            function elementInView(el) {
              var bounds = el.getBoundingClientRect();
              return bounds.top < window.innerHeight && bounds.bottom > 0;
            }

            // Replace blank placeholder image with actual image
            function updateImgSrc(img) {
              var src = img.getAttribute("data-src");
              if (src && img.getAttribute("src") != src) {
                img.setAttribute("src", src);
              }
            }

            function onIntersectionChange(entries) {
              // There may be multiple entries relating to the same container
              // (captured at different times)
              var isIntersecting = entries.reduce(function(memo, entry) {
                return memo || entry.isIntersecting;
              }, false);
              if (isIntersecting) {
                waiting = false;
                // update: don't remove -- we need the observer to trigger an update
                // when a hidden map becomes visible after user interaction
                // (e.g. when an accordion menu or tab opens)
                // observer.disconnect();
                // observer = null;
                update();
              }
            }

            function selectChildren(selector, parent) {
              return parent ?
                Array.prototype.slice.call(parent.querySelectorAll(selector)) :
                [];
            }

            // based on underscore.js
            function throttle(func, wait) {
              var timeout = null,
                previous = 0;

              function run() {
                previous = Date.now();
                timeout = null;
                func();
              }
              return function() {
                var remaining = wait - (Date.now() - previous);
                if (remaining <= 0 || remaining > wait) {
                  clearTimeout(timeout);
                  run();
                } else if (!timeout) {
                  timeout = setTimeout(run, remaining);
                }
              };
            }
          })("g-votesMap-box", {
            namespace: "g-",
            setup: window.setupInteractive || window.getComponent
          });
        </component>
      </section>
      <section class="body-text">
        <p>The system was a compromise between the nation&#x2019;s founders, who debated whether the president should be picked by Congress or through a popular vote. In more than half of the world&#x2019;s democracies, the head of state is directly
          elected by voters, according to the Pew Research Center.</p>
        <h2>The Electoral College</h2><img src="img/explainer/allotment.png" class="explainer-img hide-for-sm" id="allotment.png"> <img src="img/explainer/allotment-sm.png" class="explainer-img hide-for-lg" id="allotment.png-sm">
        <h4>Allotment of electors</h4>
        <p>Each state has as many electors as it has representatives and senators in Congress. There are two senators for each state, but the allocation of seats in the House of Representatives varies based on population.</p>
        <p>California, the most populous state, has 55 electors, while Wyoming, the least populous, only has three electoral votes.</p>
        <p>The 4 million people who live in U.S. territories, such as Puerto Rico, cannot vote in the presidential election. That&#x2019;s roughly equivalent to the number of people in the five least populous states combined.</p><img
          src="img/explainer/nomination.png" class="explainer-img hide-for-sm" id="nomination.png"> <img src="img/explainer/nomination-sm.png" class="explainer-img hide-for-lg" id="nomination.png-sm">
        <h4>Nomination of electors</h4>
        <p>Political parties select party loyalists to cast each state&#x2019;s electoral votes. The criteria for selection varies by state. There are 538 &#x201C;electors&#x201D; &#x2014; one for each vote in the Electoral College.</p><img
          src="img/explainer/election-day.png" class="explainer-img hide-for-sm" id="election-day.png"> <img src="img/explainer/election-day-sm.png" class="explainer-img hide-for-lg" id="election-day.png-sm">
        <h4>Election Day</h4>
        <p>Voters cast their ballots. Although a ballot usually has only the names of the presidential and vice presidential candidates, voters are actually voting for a group &#x2014; or &#x201C;slate&#x201D; &#x2014; of electors.</p><img
          src="img/explainer/winner-take-all.png" class="explainer-img hide-for-sm" id="winner-take-all.png"> <img src="img/explainer/winner-take-all-sm.png" class="explainer-img hide-for-lg" id="winner-take-all.png-sm">
        <h4>Winner takes all</h4>
        <p>All but two states use a winner-take-all approach: The candidate that wins the most votes in that state gets all of its electoral votes.</p>
        <p>This means campaigns tend to focus on battleground states, rather than Democratic or Republican strongholds. Battleground states include Colorado, Florida, Michigan, Nevada, New Hampshire, North Carolina, Pennsylvania and Wisconsin.</p>
        <img src="img/explainer/maine-neb.png" class="explainer-img hide-for-sm" id="maine-neb.png"> <img src="img/explainer/maine-neb-sm.png" class="explainer-img hide-for-lg" id="maine-neb.png-sm">
        <h4>Maine and Nebraska</h4>
        <p>Maine and Nebraska use a more complex district-based allocation system that could result in their combined nine electoral votes being split.</p>
        <p>In 2016, Democratic candidate Hillary Clinton won three of Maine&#x2019;s four electoral votes and Trump won one.</p><img src="img/explainer/electors-convene.png" class="explainer-img hide-for-sm" id="electors-convene.png"> <img
          src="img/explainer/electors-convene-sm.png" class="explainer-img hide-for-lg" id="electors-convene.png-sm">
        <h4>Electoral College convenes</h4>
        <p>The electors meet in December to officially cast their votes. The candidate that wins 270 electoral votes or more becomes president.</p><img src="img/explainer/rogue.png" class="explainer-img hide-for-sm" id="rogue.png"> <img
          src="img/explainer/rogue-sm.png" class="explainer-img hide-for-lg" id="rogue.png-sm">
        <h4>Going rogue</h4>
        <p>Generally, the meeting of electors is a ceremonial event where they simply rubber-stamp votes for the candidate who won their respective states. But in 2016, seven of the 538 electors voted for someone other than their state&#x2019;s
          popular vote winner, an unusually high number. Three of these seven electors voted for Colin Powell, a former U.S. secretary of state, even though they represented states that picked Clinton.</p>
        <p>Thirty-two states and the District of Columbia have laws intended to control electors from going rogue and voting for someone else. Some provide a financial penalty for these so-called &#x201C;faithless electors,&#x201D; while others call
          for the vote to be canceled and the elector replaced.</p>
        <p>In July, the <a href="https://www.reuters.com/article/us-usa-court-electoral/u-s-supreme-court-curbs-faithless-electors-in-presidential-voting-idUSKBN2471TI">U.S. Supreme Court </a>issued a decision saying states have the authority to
          punish &#x201C;faithless electors.&#x201D;</p>
        <h2>Uneven representation</h2>
        <p>The total number of electoral votes has stayed the same for decades despite changes in the population, which has led to a disparity in representation. One electoral vote in Wyoming, the least populous state, represents 193,000 people. One
          vote in Texas, the most underrepresented state, represents 763,000.</p>
      </section>
      <section class="graphic borderless pt-4">
        <h4 class="text-left">Representation in the Electoral College</h4>
        <p>If electoral votes were distributed evenly across the country, one vote would represent 610,000</p>
        <div class="representation legend-container hide-for-sm">
          <div class="legend-item"><span class="legend-circle dem"></span>Voted Dem. in 2012 and 2016</div>
          <div class="legend-item"><span class="legend-circle rep"></span>Voted Rep. in both</div>
          <div class="legend-item"><span class="legend-circle flip"></span>Flipped Rep. in 2016</div>
        </div>
        <div class="representation legend-container hide-for-lg" id="add-flex">
          <div class="legend-item"><span class="legend-circle dem"></span>Voted Dem. in 2012 and 2016</div>
          <div class="legend-item"><span class="legend-circle rep"></span>Voted Rep. in both</div>
        </div>
        <div class="representation legend-container hide-for-lg">
          <div class="legend-item"><span class="legend-circle flip"></span>Flipped Rep. in 2016</div>
        </div>
      </section>
      <section class="graphic borderless representation-container">
        <div id="representation-chart"></div>
        <div id="representation-chart-sm"></div>
      </section>
      <section class="graphic borderless pb-5 pt-5">
        <aside>
          <p>Sources: U.S. Census Bureau; Social Explorer</p>
          <p>Note: Population data based on the 2019 American Community Survey</p>
        </aside>
      </section>
      <section class="body-text">
        <p>With most states following a winner-take-all approach, it is possible to win the Electoral College but lose the national popular vote.</p>
        <p>Since 1876, four candidates have won the popular vote but lost the election. This happened in 2000, when Republican George W. Bush won the presidency despite losing the popular vote, and in 2016, when Trump pulled off a similar victory.
        </p>
      </section>
      <section class="graphic borderless pb-2 pt-5">
        <h4 class="text-left">Diverging votes</h4>
        <p>The popular and electoral vote margins of candidates who won the presidency since 1824</p>
        <div class="representation legend-container d-flex">
          <div class="legend-item"><span class="legend-circle dem"></span>Dem.</div>
          <div class="legend-item"><span class="legend-circle rep"></span>Rep.</div>
        </div>
      </section>
      <section class="graphic borderless margins-container wide">
        <div id="margins-chart"></div>
      </section>
      <section class="graphic borderless pb-5 pt-5">
        <aside>
          <p>Sources: Federal Election Commission; Clerk of the House of Representatives; U.S. Library of Congress; Encyclopedia Britannica</p>
          <p>Note: In 1824, John Quincy Adams lost the popular vote but won the presidency. This election is not included in the chart as there is no comparable data.</p>
        </aside>
      </section>
      <section class="body-text">
        <h2>What could go wrong in 2020</h2>
        <h4>Voting by mail</h4>
        <p>The number of people voting by mail has surged in 2020 because of the novel coronavirus pandemic. Mail-in votes are processed differently across the states, and some may not finish counting all the ballots on Election Day. If the race is
          close, there may not be a clear winner on election night.</p>
        <h4>Dueling slates of electors</h4>
        <p>Typically, governors certify the results in their respective states and share this information with Congress. In a closely contested state, it is theoretically possible for the governor and legislature to submit two different election
          results, leading to so-called &#x201C;dueling slates of electors.&#x201D; In 1876, dueling electors in three states deadlocked the election until a deal was brokered days before Inauguration Day.</p>
        <p>The risk of this happening is heightened in states where the legislature is controlled by a different party than the governor. Several battleground states, including Michigan, Pennsylvania and Wisconsin, have Democratic governors and
          Republican-controlled legislatures.</p>
        <p><a href="https://www.reuters.com/article/us-usa-election-electoral-college-explai/explainer-the-electoral-college-and-the-2020-u-s-presidential-race-idUSKBN26X19O">According to legal experts</a>, it is unclear in this scenario whether
          Congress should accept the governor&#x2019;s electoral slate or not count the state&#x2019;s electoral votes at all.</p>
        <h4>Electoral College tie</h4>
        <p>One flaw of the Electoral College system is that it could result in a 269-269 tie. If that occurs, a newly elected House of Representatives would decide the fate of the presidency on Jan. 6, with each state voting as a unit, as required by
          the 12th Amendment of the U.S. Constitution.</p>
        <p>The requirement that each state votes as a unit currently favors Trump&#x2019;s Republican Party. There are 26 states with more Republican members in the House than Democratic members.</p>
        <p>The composition of the House will change on Nov. 3, when all 435 House seats are up for grabs.</p>
      </section>
      <section class="end-notes">
        <h2>Sources</h2>
        <p>U.S. Census Bureau; Social Explorer; Federal Election Commission; Clerk of the House of Representatives; U.S. Library of Congress; Encyclopedia Britannica</p>
        <h2>Additional reporting by</h2>
        <p>Tom Hals</p>
        <h2>Editing by</h2>
        <p>Christine Chan, Noeleen Walder, Rosalba O&#x2019;Brien and Howard Goller</p>
      </section>
    </article>
    <!-- <component is="script" src="//graphics.thomsonreuters.com/pym.min.js"></component>
    <component is="script">
      window.childFrame = new pym.Child({
        polling: 500
      });
    </component> -->
    <!-- <script src="./app.aa217c7f69d29b1a2e41.js"></script>
    <script src="./embedTools.bb837b0a057bd5968dea.js"></script> -->
    <component is="script" src="reuters/scripts/app-reuters.js"></component>
  </div>
</template>

<script>
  export default {
    mounted() {},
    methods: {}
  }
</script>
